export function getRowKey(row, index) {
  return row.id || `${row.date || ""}-${row.car_number || ""}-${index}`;
}

export function normalizeCarNumber(value = "") {
  return String(value || "").replace(/\s+/g, "").trim();
}

export function getCarNumberMatchKey(value = "") {
  const normalized = normalizeCarNumber(value);
  const digits = normalized.replace(/\D/g, "");
  return digits.length >= 4 ? digits.slice(-4) : normalized;
}

export function hasNonNumericCarNumber(value = "") {
  const carNumber = normalizeCarNumber(value);
  return carNumber !== "" && /[^0-9]/.test(carNumber);
}

export function isExternalCarNumber(value = "", carIndex = new Map()) {
  const carNumber = normalizeCarNumber(value);
  if (!carNumber) return false;
  if (hasNonNumericCarNumber(carNumber)) return true;

  const car = carIndex.get(getCarNumberMatchKey(carNumber));
  return !!car && car.direct !== true;
}

export function timeToMinutes(value = "") {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return hour * 60 + minute;
}

export function getTimeRange(row) {
  const start = timeToMinutes(row.time);
  if (start === null) return null;

  const arrival = timeToMinutes(row.arrival_time);
  if (arrival === null) {
    return {
      start,
      end: start + 60,
      hasExplicitArrival: false,
    };
  }

  const end = arrival <= start ? arrival + 24 * 60 : arrival;
  return {
    start,
    end,
    hasExplicitArrival: true,
  };
}

export function rangesOverlap(a, b) {
  return a.start < b.end && b.start < a.end;
}

export function hasReservationTimeConflict(a, b) {
  const aMinutes = timeToMinutes(a?.time);
  const bMinutes = timeToMinutes(b?.time);
  if (aMinutes === null || bMinutes === null) return false;

  const aRange = getTimeRange(a);
  const bRange = getTimeRange(b);
  const hasRange = aRange?.hasExplicitArrival || bRange?.hasExplicitArrival;

  return hasRange && aRange && bRange
    ? rangesOverlap(aRange, bRange)
    : Math.abs(aMinutes - bMinutes) <= 60;
}

export function getCarConflictInfo(rows = [], carIndex = new Map()) {
  const rowLevels = new Map();
  const groups = new Map();
  let hasOverlap = false;

  rows.forEach((row, index) => {
    const rowKey = getRowKey(row, index);
    if (isExternalCarNumber(row.car_number, carIndex)) {
      rowLevels.set(rowKey, "external");
      return;
    }

    const carNumber = normalizeCarNumber(row.car_number);
    if (!row.date || !carNumber) return;

    const groupKey = `${row.date}__${carNumber}`;
    if (!groups.has(groupKey)) groups.set(groupKey, []);
    groups.get(groupKey).push({
      row,
      index,
      key: rowKey,
      minutes: timeToMinutes(row.time),
      range: getTimeRange(row),
    });
  });

  groups.forEach((items) => {
    if (items.length < 2) return;

    items.forEach((item) => {
      if (!rowLevels.has(item.key)) rowLevels.set(item.key, "same-day");
    });

    for (let i = 0; i < items.length; i += 1) {
      for (let j = i + 1; j < items.length; j += 1) {
        const a = items[i];
        const b = items[j];
        if (a.minutes === null || b.minutes === null) continue;

        const hasRange = a.range?.hasExplicitArrival || b.range?.hasExplicitArrival;
        const isOverlapping = hasRange && a.range && b.range
          ? rangesOverlap(a.range, b.range)
          : Math.abs(a.minutes - b.minutes) <= 60;

        if (isOverlapping) {
          rowLevels.set(a.key, "overlap");
          rowLevels.set(b.key, "overlap");
          hasOverlap = true;
        }
      }
    }
  });

  return { rowLevels, hasOverlap };
}
